/**
 * Turning a stored reference into something a browser will load.
 *
 * Decision 0017, point 2. The renderer refuses any media URL that is not
 * `http(s)` — `packages/runtime/test/media.test.ts` pins six schemes against
 * exactly that — and the guard is right: a media value is data, and data is
 * where an untrusted URL would arrive from. So the reference never reaches the
 * renderer. The HOST reads the bytes out of its own store, mints an object URL
 * for them, and hands the renderer that. What arrives is a URL this page
 * created, which is the one class of URL that cannot have come from anywhere
 * else.
 *
 * ## Why there is no leak to fix
 *
 * 0017 point 3 says object URLs must be revoked when the pass that minted them
 * is replaced, because this app repaints on every edit and an object URL pins
 * its blob in memory until revoked — so a per-pass mint would leak a copy of
 * every visible image per keystroke.
 *
 * That is the right worry about the wrong design. **A blob id IS its content**,
 * so the URL for an id can never go stale: mint it once, keep it, and a
 * thousand repaints reuse the same string. The cache is keyed by id rather
 * than by pass, which turns "revoke on every repaint" into "revoke when an id
 * stops being on screen" — and makes the common case, typing beside an image,
 * allocate nothing at all.
 *
 * `sweep()` is what bounds it: after a pass, ids that were not referenced are
 * revoked. Called AFTER the DOM is replaced, never before — an `<img>` in the
 * outgoing DOM may still be decoding, and a revoked URL under it is a broken
 * image rather than a freed byte.
 */
import type { BlobId, BlobStore, BoundValues } from './deps.js';
import { blobRefIn, type BlobLookup } from '@morph/store';
import { hostResolved } from '@morph/runtime';
import { DERIVATIVE_OF, type TLayoutSpec } from '@morph/spec';
import { roleReferences } from '@morph/generation';

/**
 * The URL inside whatever `projection` handed back for a media role.
 *
 * **A bare string, usually.** The store holds `{ k: 'media', v: { blob, mime } }`
 * and `projection` flattens it, so what arrives here is
 * `"https://image.tmdb.org/..."` — not the descriptor. The first version of
 * this file checked for an object with a `.blob` and therefore matched nothing
 * at all: every reference sailed through unresolved, the renderer refused the
 * `morph-blob:` string it then saw, and a dropped image silently did not draw.
 * The renderer accepts both shapes, so this does too — but the string is the
 * live path and the object one is the defensive branch, not the other way
 * round.
 *
 * Note what the flattening costs: **the mime is gone.** That is fine, and
 * better than fine — the mime this needs is the one the bytes were stored
 * under, which the blob store knows, rather than one carried alongside a
 * reference where it could disagree.
 */
const urlIn = (v: unknown): string | undefined =>
  typeof v === 'string' ? v
    : v && typeof v === 'object' && typeof (v as { blob?: unknown }).blob === 'string'
      ? (v as { blob: string }).blob
      : undefined;

/**
 * Which roles a pass must resolve for one spec.
 *
 * The roles the spec REFERENCES, plus the derivatives the renderer draws
 * beside them without a binding of their own.
 *
 * That second half is not a nicety, and finding it is the whole of *"what could
 * this change break?"*. `render-html.ts` reads `obj?.['media.poster-frame']`
 * directly inside the video branch — no `n.role`, no binding, nothing
 * `roleReferences` can see — so a filter built from the spec alone would have
 * resolved every video and left its poster as a `morph-blob:` string the
 * runtime refuses. Every video on the site would have lost the frame it shows
 * before it plays, and every unit suite would have stayed green.
 *
 * `DERIVATIVE_OF` is where that relationship already lives: `media.thumb` is
 * of `media.hero`, `media.poster-frame` is of `media.video`. Reading it here
 * rather than naming the poster means a third derivative arrives covered.
 *
 * Note the DIRECTION. A bound source pulls in its derivative, which is cheap
 * and is what the renderer may draw beside it. A bound derivative does not pull
 * in its source — that is the entire point of the narrowing, and inverting it
 * would restore the full-source read `media.thumb` exists to avoid.
 */
/**
 * The schema keys whose role is EVALUATED, never drawn.
 *
 * @Codex, P2 on `7788a4c`: *"When a valid layout references source media only
 * in a selector — such as `when: { has: 'media.hero' }` — but renders
 * `media.thumb`, `roleReferences()` includes the selector role and this set
 * tells `resolve()` to load the full source blob. Selectors are already
 * evaluated by the store and never need an object URL, so a cold collection can
 * reread and pin up to 64 MiB per item despite binding only thumbnails."*
 *
 * The P1's own cost, coming back through the door built to close it — and it
 * would not have shown up in a harness, because a selector on `media.hero` is a
 * legal arrangement nothing in `e2e/` happens to build.
 *
 * Three keys, and each is evaluated over STORED values by a layer that never
 * asks for a URL: `render-html.ts` reads `obj?.[t.when.has]` to test presence,
 * and grouping is the query layer's. `isPopulated` answers the same for a
 * `morph-blob:` string as for a resolved one, so the selector is unaffected by
 * not resolving it.
 *
 * `via` is deliberately NOT here even though a relation role is never itself a
 * blob: the nested walk needs it in the set to descend into an expanded
 * relation target at all, and the media inside that target is exactly what the
 * other half of this review was about.
 */
const EVALUATED_NOT_DRAWN: ReadonlySet<string> = new Set(['has']);

/**
 * A role reference reached through a node's `query`, and therefore never drawn.
 *
 * @Codex, P2 on `802b4b2`: *"When a legal query sorts, filters via
 * `where.role`, or aggregates on `media.hero` while rendering only
 * `media.thumb`, this last-segment filter retains the source because those
 * query references end in `/role`, just like rendered bindings … the final
 * head's `SortKey`, `Predicate`, and aggregate schemas all use the generic
 * `role` key, so filtering only `has`, `groupBy`, and `subGroupBy` does not
 * cover all store-evaluated roles."*
 *
 * Exact. The list this replaces named the three query keys somebody thought of,
 * and `Query` has six more that carry a `RoleRef`:
 *
 * ```
 * covered by a name list   groupBy   subGroupBy
 * NOT covered              sort/N/role   filter/…/where/role   aggregate/N/role
 *                          traverse/via
 * ```
 *
 * So this filters by ANCESTRY instead. `schema.ts` puts the whole sublanguage
 * behind one key — `query: Type.Optional(Query)` — so *"reached through a
 * query"* is a fact about the path rather than a list to keep in step, and a
 * seventh role-bearing key added to `Query` tomorrow is covered by the same
 * line. That is the same property the derived harness set has, and it is the
 * repair for exactly the defect `CLAUDE.md` names one section earlier: an
 * allowlist derived from a declaration is only as complete as somebody's
 * reading of that declaration.
 *
 * **`traverse/via` goes and the `node-slot` `via` stays**, which is the case a
 * name list cannot express at all: `via` appears at both, once as a graph walk
 * the store performs and once as the relation a nested media value hangs off.
 * The nested walk needs the second in the set to descend into an expanded
 * relation target — the other half of the same review — and must not be handed
 * the first.
 *
 * **And `viewState` is the same gap one subtree over**, found by reading
 * `roleReferences` rather than the fixture: it walks `spec.viewState` as well as
 * `spec.root`, and a `ViewStateSlot` is *"a named, typed control whose value
 * drives queries"* — `{ name, role, op }`, a predicate's left-hand side, drawn
 * nowhere. Its path is `/viewState/N/role`, which no amount of `/query/` covers.
 * A slot on `media.hero` would have pinned every source in the pool through a
 * control, which is the review's own finding arriving by a second route.
 */
const storeEvaluated = (path: string): boolean =>
  path.includes('/query/') || path.startsWith('/viewState/');

export function resolvableRoles(spec: TLayoutSpec): ReadonlySet<string> {
  const roles = new Set(
    roleReferences(spec)
      // Position, not role name: `roleReferences` builds the path as
      // `${path}/${key}`. A role drawn in one place and selected on in another
      // is still resolved, which is the only safe way round — the cost of
      // over-resolving is a read, and the cost of under-resolving is a blank
      // image. `has` is a template `when` clause rather than a query, so it
      // needs its own name; everything store-evaluated sits under `/query/`.
      .filter((r) => !storeEvaluated(r.path)
        && !EVALUATED_NOT_DRAWN.has(r.path.slice(r.path.lastIndexOf('/') + 1)))
      .map((r) => r.role as string));
  for (const [derivative, source] of Object.entries(DERIVATIVE_OF)) {
    if (source && roles.has(source)) roles.add(derivative);
  }
  return roles;
}

export interface BlobUrls {
  /**
   * The same objects, with every internal reference swapped for a URL.
   *
   * Returns new objects rather than mutating: these values came out of
   * `projection`, and writing into them would make what the renderer draws
   * differ from what the store holds, in a way nothing downstream could detect.
   *
   * ACCUMULATES. A pass may resolve more than one set — the demo resolves the
   * content and the panel's siblings, which are different collections — and
   * they are what is on screen TOGETHER. The first version reset its
   * bookkeeping at the top of `resolve`, so the second call wiped what the
   * first had recorded and `sweep()` revoked a URL the panel was still drawing.
   * A pass ends at `sweep`, not at a `resolve`.
   */
  resolve(
    objects: readonly BoundValues[],
    wanted?: ReadonlySet<string>,
  ): Promise<BoundValues[]>;
  /** Release URLs nothing resolved since the last sweep, and start a new pass. */
  sweep(): void;
  /** Every URL, released. For teardown. */
  releaseAll(): void;
  /**
   * Mint a URL from bytes the caller already holds, instead of reading them
   * back out of the store.
   *
   * The 64 MiB ceiling's freeze was this read. Measured, deployed, on
   * `main.ae7f59ba6557.js`: the repaint after ingesting a 64 MiB file blocked
   * for **3,290-4,386ms** against decision `0042`'s 1,824ms — and locally the
   * same repaint is 254ms, of which 91ms is reading the file back out of
   * IndexedDB and 42ms is `createObjectURL`. A second repaint with the URL
   * already cached is **0ms**.
   *
   * So the render is free and the read is everything, and the read is of bytes
   * that were in memory a moment earlier: `ingestOne` has the `Uint8Array`,
   * stores it, and then the renderer fetches all of it back to make a URL.
   *
   * Priming skips that. It cannot change what is drawn — the id is the digest
   * of these bytes, so the URL minted from them is the URL that read-back would
   * have produced.
   */
  /** `true` when this call MINTED the URL. `false` means one already existed
   *  for these exact bytes and this type — somebody else's, possibly on
   *  screen — and the caller has no claim on it. See `release`. */
  prime(id: BlobId, bytes: Uint8Array<ArrayBuffer>, mime: string): boolean;
  /**
   * Give back a primed URL nothing is going to draw.
   *
   * @Codex, P2 on #184: *"When a person navigates away while a source write is
   * pending, the navigation's render can call `sweep()` before `ingestOne()`
   * finishes; `onStored` then primes the source URL, but `attachFile()` refuses
   * the now-wrong target and schedules no later render … it remains cached and
   * pins the source Blob indefinitely."*
   *
   * `prime()`'s own comment says a primed URL nobody draws *"should be
   * collected like any other"* — and that is true only if another pass runs.
   * An abandoned ingest is exactly the case where none does, and at the 64 MiB
   * ceiling each one pins that much until the tab closes.
   *
   * **Refuses to revoke a URL the last pass drew.** The digest is the id, so a
   * second ingest of bytes already on screen primes nothing (`prime` returns
   * early) and the id in hand belongs to somebody else's live URL. `seen` is
   * the same rule `sweep()` uses, applied to one id.
   *
   * **And `seen` alone is not enough, which is this fix one round later.**
   *
   * @Codex, P2 on #184: *"a normal render calls `blobUrls.sweep()` and resets
   * `seen` even though the retained URLs remain in the current DOM. If the
   * same bytes are ingested again and the attachment is then refused or
   * superseded, `prime()` reuses that existing URL, but this guard sees an
   * empty `seen` set and revokes the URL underneath the currently displayed
   * image, audio, or video, with no later render scheduled to repair it."*
   *
   * Right: `seen` means *drawn since the last sweep*, and a sweep empties it
   * while the URLs it kept are still on screen. So between a sweep and the
   * next render every live URL looks releasable, and the release-on-refusal
   * path I added this morning is exactly the caller that looks.
   *
   * The honest condition is not "nothing has drawn it" but **"I minted it"**,
   * which is what `prime`'s return value now says. Both checks stay: minting
   * gives the caller a claim, `seen` withdraws it again if a render drew the
   * URL between the prime and the refusal.
   */
  release(at: { readonly id: BlobId; readonly mime: string }): void;
}

/** What a resolution yields: the object URL, and the media type of the row that
 *  actually resolved. Returned together because the caller needs both and
 *  recovering the second by searching the first was O(N) per field per repaint. */
interface Resolved { readonly url: string; readonly mime: string }

export function createBlobUrls(blobs: BlobStore): BlobUrls {
  const urls = new Map<string, string>();
  /**
   * Keyed by the accepted PAIR, not by the digest — decision `0047`.
   *
   * @Codex, P2 on `ef26359`: *"this call reduces it to the digest, and
   * `urlFor()` subsequently loads the blob using the store's first MIME. The
   * forged typed reference therefore resolves instead of remaining unresolved
   * as accepted decision 0047 requires."*
   *
   * Parsing the selector is only half of that. The cache was keyed by digest,
   * so even a correctly parsed pair would have collided with the other accepted
   * type for the same bytes and been handed a URL carrying the wrong MIME — the
   * same wrong answer, arrived at one step later. `0047` says one digest may
   * carry several accepted rows, so the unit of caching is the row.
   */
  let seen = new Set<string>();

  const mimes = new Map<string, string>();

  /** `${digest};${accepted mime}` — the row a resolution belongs to. */
  const pairKey = (id: BlobId, mime: string) => `${id};${mime}`;

  /**
   * Reads in flight, so one id is fetched once and installed once.
   *
   * @Codex, P2 on #184: *"When `prime()` runs while an earlier `urlFor(id)` is
   * awaiting `blobs.get(id)` — for example, when the same source is re-ingested
   * during a render — this installs one object URL, but the pending resolver
   * later unconditionally installs a second URL for the same id. The first URL
   * is overwritten in `urls` and can therefore never be revoked by `sweep()` or
   * `releaseAll()`, leaving the Blob pinned."*
   *
   * Exact, and it is a leak `prime()` created: before it there was no second
   * writer of `urls`, so an unconditional `urls.set` after an await was safe by
   * accident. `prime()` exists to avoid reading a 64 MiB file back out of the
   * store, and the blob it pins is that same 64 MiB.
   *
   * Two writers deserve two fixes and both are here. `pending` shares one read
   * between concurrent callers, which is the cheaper half and removes the
   * `urlFor`-against-`urlFor` case entirely. The re-check after the await is
   * what covers `prime()`, which does not go through `pending` and must not:
   * its whole point is having the bytes already.
   *
   * The loser is not revoked, it is never MINTED. Whoever installed first has
   * the same bytes by construction — the id is their digest — so there is no
   * URL to choose between, and nothing on screen can be drawing a URL that was
   * never handed out.
   */
  const pending = new Map<string, Promise<Resolved | undefined>>();
  /**
   * A legacy digest-only lookup, to the pair it resolved to.
   *
   * @Codex, P2 on `1c4d8b7`: *"When a migrated vault renders a legacy
   * `morph-blob:<digest>` reference, `at` is a string, so this conditional
   * skips the cached URL on every pass. Even after the first resolution has
   * cached the resolved `(digest, MIME)` pair, each subsequent repaint calls
   * `blobs.get(at)` again; with IndexedDB that structured-clones the complete
   * payload, so typing beside a legacy ceiling-size image can repeatedly reread
   * 64 MiB and reintroduce the multi-second stall this cache is intended to
   * prevent."*
   *
   * The two-key scheme was right and stopped one step short: `urls` is keyed by
   * the resolved pair precisely so a legacy reference and a typed one share an
   * object URL, and then the legacy side had no way to ask. This is that way.
   *
   * An entry can outlive its URL — `sweep()` revokes by pair key — so it is
   * dropped there too, and a stale one would only cost a re-read rather than a
   * wrong answer.
   */
  const resolvedPair = new Map<string, string>();

  /**
   * The pair keys `prime()` MINTED and nothing has drawn yet.
   *
   * The only thing `release()` may revoke, and the invariant is in here rather
   * than in the caller on purpose. `seen` used to stand in for it and cannot:
   * `seen` means *drawn since the last sweep*, and `sweep()` empties it while
   * keeping every URL that was drawn — so in the window between a sweep and
   * the next render, every live URL looks releasable. @Codex, P2 on #184.
   *
   * Three edges keep it honest, and each is one line at the place it happens:
   * a draw removes the key, because whatever is on screen outranks an
   * ingest's claim on it; a `sweep()` that revokes removes it, because there
   * is nothing left to give back; and `prime()` on an existing URL adds
   * nothing, because that URL is not this caller's.
   */
  const minted = new Set<string>();

  /**
   * The minted keys that have already survived one `sweep()`.
   *
   * `prime()` mints during an ingest, and the render that will DRAW those bytes
   * has not resolved yet — so at the end of the pass the ingest completed in,
   * the key is legitimately unseen. Sweeping it there revokes the URL a moment
   * before the thing it exists for asks for it, and the resolve then reads the
   * whole file back out of IndexedDB: exactly the read `prime()` was written to
   * remove, arriving one pass later than the one task `0125` measured.
   *
   * Measured on `main.16756d6ebf09.js` with a probe in each of the three
   * functions, on the 64 MiB ceiling fixture:
   *
   * ```
   * PROBE prime          80f5e45e…;audio/wav   ok:true   67,108,864 bytes
   * PROBE sweep revokes  80f5e45e…;audio/wav             ← the pass ends here
   * PROBE urlFor MISS    80f5e45e…;audio/wav             ← 64 MiB read back
   * ```
   *
   * Same key in all three lines. `prime()` returned `true`, so the URL was
   * minted; the very next sweep revoked it; the first lookup missed.
   *
   * **One sweep of grace, not an exemption.** `prime()`'s own comment argues
   * that a primed URL nobody draws *"should be collected like any other"*, and
   * that is still true — this only says the collection may not happen in the
   * pass that was already under way when the bytes arrived. A prime nothing
   * ever draws is revoked by the second sweep, so the backstop `release()`
   * leans on is intact and the bound is two passes rather than none.
   */
  let mintedSwept = new Set<string>();

  /**
   * The URL AND the type of the row that resolved, together.
   *
   * @Codex, P2 on `1c4d8b7`: *"every resolution callback materializes the
   * entire `urls` map and linearly searches it to recover the MIME. Once the
   * cache is warm, each repaint still performs this scan for every field,
   * making an otherwise cached render O(N²) in allocations and comparisons."*
   *
   * The caller wanted the pair and was handed half of it, so it reconstructed
   * the other half by searching. Returning both is smaller than the search and
   * smaller than a third map keyed by URL, which would be one more thing for
   * `sweep()` to keep in step.
   */
  const urlFor = async (at: BlobLookup): Promise<Resolved | undefined> => {
    const id = typeof at === 'string' ? at : at.id;
    /**
     * Two keys, deliberately.
     *
     * `pending` is keyed by the LOOKUP, because that is all that is known
     * before the store answers — a legacy digest-only reference does not name
     * its type until the row is found. `urls` and `seen` are keyed by the
     * RESOLVED pair, so a legacy reference and a typed one that select the same
     * accepted row share one object URL instead of pinning the bytes twice.
     */
    const lookup = typeof at === 'string' ? at : pairKey(at.id, at.mime);
    // A typed lookup IS the pair key; a legacy one has to ask what it resolved
    // to last time, which is what `resolvedPair` remembers.
    const key = typeof at === 'string' ? resolvedPair.get(lookup) : lookup;
    if (key !== undefined) {
      const cached = urls.get(key);
      // Drawn, so no ingest has a claim on it any more — see `minted`.
      if (cached) {
        seen.add(key); minted.delete(key);
        return { url: cached, mime: mimes.get(key) ?? '' };
      }
    }
    const already = pending.get(lookup);
    if (already) return already;
    const read = (async (): Promise<Resolved | undefined> => {
      // The WHOLE pair crosses the store boundary. `0047`: the type is a
      // selector the store must authorise, so a forged pair returns nothing
      // here rather than resolving through some other accepted row.
      const stored = await blobs.get(at);
      // A reference whose bytes are gone resolves to nothing, and the renderer
      // draws its empty state — which is the honest answer. Minting a URL for an
      // empty blob would draw a broken image instead, and a broken image is a
      // claim that something is there.
      if (!stored) return undefined;
      // The row's own type decides the key, which is why this is read AFTER the
      // store answers: a legacy reference does not name it.
      const resolved = pairKey(id, stored.mime);
      // Drawn — including the case where the URL under it was primed by an
      // ingest whose refusal has not arrived yet. See `minted`.
      seen.add(resolved); minted.delete(resolved);
      // Remembered for the legacy side, which cannot name the pair until now.
      if (typeof at === 'string') resolvedPair.set(lookup, resolved);
      // Installed while this read was out. Nothing between this line and the
      // `urls.set` below awaits, so no third writer can land in the gap.
      const now = urls.get(resolved);
      if (now) return { url: now, mime: stored.mime };
      const url = URL.createObjectURL(new Blob([stored.bytes], { type: stored.mime }));
      urls.set(resolved, url);
      mimes.set(resolved, stored.mime);
      return { url, mime: stored.mime };
    })();
    pending.set(lookup, read);
    try {
      return await read;
    } finally {
      pending.delete(lookup);
    }
  };

  /**
   * How deep an expanded projection goes, plus one.
   *
   * A relation target is expanded one level — `inlineRelations` generates a
   * `single` lens for it and the store's expansion is bounded — so 3 is slack
   * rather than a limit anything reaches. It is here because this walk runs on
   * every repaint over values that came out of a person's store, and an
   * unbounded recursion over data is the one this repository has already paid
   * for in `image-size.ts`.
   */
  const MAX_DEPTH = 3;

  /**
   * One value, resolved in place through a copy, however deeply it sits.
   *
   * @Codex, P2 on `4261caa`: *"When a `node-slot` hosts a media node for an
   * expanded related object, the relation value is a nested `BoundValues`
   * object whose `media.*` entry contains the internal reference. This loop
   * examines only the subject's top-level values, so `urlIn()` sees the whole
   * related object, skips it, and leaves the nested `morph-blob:` string
   * unchanged; the runtime then refuses that untrusted scheme and the related
   * image, audio, or video renders empty."*
   *
   * Right, and the shape of the miss is worth naming: `urlIn` answers
   * `undefined` for a related OBJECT exactly as it does for a number, so the
   * old loop could not tell "nothing to resolve here" from "a container it did
   * not open". A `continue` that means both is a guard that stops early — the
   * shape `CLAUDE.md` lists as (d).
   *
   * Returns `undefined` when nothing under this value needs resolving, so an
   * unchanged object is returned by identity and a repaint allocates nothing
   * for the values it is not touching.
   */
  const resolveInto = (
    value: unknown,
    wanted: ReadonlySet<string> | undefined,
    work: Array<Promise<void>>,
    write: (v: unknown) => void,
    depth: number,
  ): boolean => {
    // The WHOLE reference, selector included. `blobIdIn` here reduced a typed
    // reference to its digest, so a forged `;video/mp4` on an audio-only digest
    // resolved through the audio row — exactly the reinterpretation `0047`
    // refuses.
    const at = blobRefIn(urlIn(value));
    if (at) {
      const lookup: BlobLookup = at.mime === undefined
        ? at.id : { id: at.id, mime: at.mime };
      work.push(urlFor(lookup).then((got) => {
        // MARKED, not merely swapped. The renderer refuses every URL scheme but
        // http(s), deliberately, and an object URL is `blob:`. `hostResolved`
        // is a symbol nothing arriving through `projection` can carry, so the
        // renderer can tell a URL this page minted from a string that came in
        // with the data — the distinction decision 0017 rejected widening the
        // guard to blur.
        //
        // The mime of the row that actually resolved — for a legacy reference
        // that is the one the store recorded, not one guessed here.
        //
        // Absent bytes leave the key OFF rather than setting an empty string:
        // `isPopulated` treats '' as absent anyway, and a key that is present
        // and empty is a different fact from one that is not there.
        write(got ? hostResolved(got.url, got.mime) : undefined);
      }));
      return true;
    }
    if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') return false;
    if (Array.isArray(value)) {
      let copy: unknown[] | undefined;
      value.forEach((entry, i) => {
        if (resolveInto(entry, wanted, work, (v) => {
          copy ??= [...value];
          if (v === undefined) copy[i] = undefined; else copy[i] = v;
        }, depth + 1)) copy ??= [...value];
      });
      if (!copy) return false;
      write(copy);
      return true;
    }
    const record = value as Record<string, unknown>;
    let copy: Record<string, unknown> | undefined;
    for (const [key, nested] of Object.entries(record)) {
      if (wanted && !wanted.has(key)) continue;
      if (resolveInto(nested, wanted, work, (v) => {
        copy ??= { ...record };
        if (v === undefined) delete copy[key]; else copy[key] = v;
      }, depth + 1)) copy ??= { ...record };
    }
    if (!copy) return false;
    write(copy);
    return true;
  };

  return {
    async resolve(objects, wanted) {
      // Collected first, resolved together: a gallery of forty images should
      // not be forty sequential IndexedDB round trips on the render path.
      const work: Array<Promise<void>> = [];
      const out = objects.map((obj) => {
        let copy: Record<string, unknown> | undefined;
        for (const [key, value] of Object.entries(obj)) {
          /**
           * The roles the SPEC binds, and nothing else.
           *
           * @Codex, P1 on `4261caa`: *"each projected object contains every
           * populated manifest role, so an ingested image or video carries both
           * its full source and its derivative into this call … a single
           * ceiling-size source therefore incurs the multi-second IndexedDB
           * read and pins 64 MiB that the derivative was introduced to avoid,
           * and a collection repeats that cost per row."*
           *
           * `media.thumb` exists precisely so a card does not pay for
           * `media.hero`, and resolving both made the derivative a second cost
           * rather than a saving. `wanted` is the set of roles reachable from
           * the spec about to be painted; `undefined` means resolve everything,
           * which is what a caller with no spec in hand gets.
           */
          if (wanted && !wanted.has(key)) continue;
          if (resolveInto(value, wanted, work, (v) => {
            copy ??= { ...obj };
            if (v === undefined) delete copy[key]; else copy[key] = v;
          }, 1)) copy ??= { ...obj };
        }
        return copy ?? obj;
      });
      await Promise.all(work);
      return out as BoundValues[];
    },

    sweep() {
      for (const [key, url] of urls) {
        if (seen.has(key)) continue;
        // Minted during the pass that is ending, and not yet offered to a
        // render. See `mintedSwept`: the draw is one pass away, and revoking
        // here is what put a 64 MiB read back on the ingest path.
        if (minted.has(key) && !mintedSwept.has(key)) continue;
        URL.revokeObjectURL(url);
        urls.delete(key);
        // The legacy alias goes with the URL it pointed at, or it accumulates
        // for the life of the page. A stale one costs a re-read rather than a
        // wrong answer, which is why this is tidiness and not correctness.
        for (const [from, to] of resolvedPair) if (to === key) resolvedPair.delete(from);
        mimes.delete(key);
        // Revoked, so there is nothing left for an ingest to give back.
        minted.delete(key);
      }
      // Every key still minted has now been through a sweep, so the next one
      // collects it. Rebuilt from `minted` rather than added to, so a key that
      // was drawn, released or revoked leaves both sets in one step.
      mintedSwept = new Set(minted);
      seen = new Set();
    },

    releaseAll() {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
      seen = new Set();
      minted.clear();
    },

    release(at) {
      const key = pairKey(at.id, at.mime);
      // Minted by a `prime()` and undrawn since. That single set replaces the
      // pair of checks that stood here — `urls.has(key) && !seen.has(key)` —
      // which was right within one pass and wrong across a `sweep()`, because
      // a sweep empties `seen` while keeping every URL the pass drew.
      if (!minted.has(key)) return;
      URL.revokeObjectURL(urls.get(key)!);
      urls.delete(key);
      mimes.delete(key);
      minted.delete(key);
      // `mintedSwept` is a subset of `minted` and is kept one everywhere, so a
      // reader does not have to know which of the two a stale key could sit in.
      // `prime()` deletes it too, and that is the one that repairs the defect;
      // this is the invariant the repair should not have to depend on.
      mintedSwept.delete(key);
    },

    prime(id, bytes, mime) {
      // Never replace a live URL: something on screen may be drawing it, and
      // revoking under a playing <audio> stops it. A cached URL is already the
      // right answer for this id, because the id IS the digest of these bytes.
      const key = pairKey(id, mime);
      // `false`, and the caller must act on it: the URL that exists is not
      // this caller's to give back. `seen` cannot tell them apart, because a
      // sweep empties it while the URLs it kept are still drawn.
      if (urls.has(key)) return false;
      /**
       * A NEW URL has never been swept, whatever the last one under this key
       * did.
       *
       * @Codex, P2 on `9bd1b63`: *"When a primed URL survives its first sweep,
       * is subsequently released after a superseded or refused ingest, and the
       * same `(id, mime)` is primed again before another sweep, `mintedSwept`
       * still contains the key from the previous URL. The next sweep therefore
       * treats the newly minted URL as already having received its grace pass
       * and revokes it before the attachment render can resolve it, causing the
       * ceiling-size source to be read back from IndexedDB again."*
       *
       * Exact, and it is the grace defeating itself: the key is the identity of
       * the BYTES, and `mintedSwept` was using it as the identity of an
       * incarnation. Two ingests of one file, the first abandoned, and the
       * second pays the read the whole mechanism exists to remove.
       */
      mintedSwept.delete(key);
      urls.set(key, URL.createObjectURL(new Blob([bytes], { type: mime })));
      mimes.set(key, mime);
      minted.add(key);
      // NOT marked as seen. `sweep()` revokes what the last pass did not draw,
      // and a primed URL nothing draws should be collected like any other —
      // priming is an optimisation, not a claim that it is in use.
      return true;
    },
  };
}
